import { injectable } from 'inversify';
import { FilterSelectionState } from './FilterSelectionState';
import { FilterKey, IFilterPayload } from '@studyportals/search-filters';
import { FilterOptions } from '../options/FilterOptions';
import { FilterSelectionStateChangedRequestedEvent } from '../events/FilterSelectionStateChangedRequestedEvent';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { EventAggregationService } from '@/platform/EventAggregationService';
import { EmbeddedStateChangedEvent } from '@/url/events/EmbeddedStateChangedEvent';
import { FilterSelectionStateCommitIgnoredEvent } from '../events/FilterSelectionStateCommitIgnoredEvent';
import { FilterSelectionStateCommittedEvent } from '../events/FilterSelectionStateCommittedEvent';

@injectable()
export class FilterSelection implements ISubscriber<EmbeddedStateChangedEvent> {
	public state: FilterSelectionState;

	private readonly filterOptions: FilterOptions;
	private eventAggregationService: EventAggregationService;

	public constructor(filterOptions: FilterOptions, eventAggregationService: EventAggregationService) {
		this.filterOptions = filterOptions;
		this.state = new FilterSelectionState();
		this.eventAggregationService = eventAggregationService;

		this.eventAggregationService.subscribeTo(EmbeddedStateChangedEvent.EventType, this);
	}

	public notify(event: EmbeddedStateChangedEvent): void {
		this.commit(event.state.filterSelectionState, event.correlationId);
	}

	public isAnySelectedFor(key: FilterKey): boolean {
		return this.state.reactive.isAnySelectedFor(key);
	}

	public getFor(key: FilterKey): readonly string[] {
		return this.state.reactive.getFor(key);
	}

	public getKeys(): readonly FilterKey[] {
		return this.state.reactive.identifySelectedFilterKeys();
	}

	public clearAll(): void {
		const state = new FilterSelectionState();
		this.publishChangeRequested(state);
	}

	public clearFor(keys: FilterKey[]): void {
		let state = this.state;
		state = state.clearFor(keys, state);

		this.publishChangeRequested(state);
	}

	public clearSelection(payload: IFilterPayload[]): void {
		let state = this.state;
		state = state.removeSelection(payload, state);

		this.publishChangeRequested(state);
	}

	public replaceFor(other: FilterSelectionState): void {
		this.publishChangeRequested(this.normalize(this.state.mergeWith(other)));
	}

	public initializeFrom(other: FilterSelectionState): void {
		let initialState = this.normalize(this.state.mergeWith(other));

		const requiredFilters = this.filterOptions.identifyRequiredFilters();
		const missingFilters = requiredFilters.filter((key) => !this.isAnySelectedFor(key));

		for (const key of missingFilters) {
			const values = this.filterOptions.getFor(key).map((option) => option.optionValue);
			initialState = initialState.addValuesFor(key, values);
		}

		this.state.reactive.replaceWith(initialState);
	}

	public normalize(state: FilterSelectionState): FilterSelectionState {
		for (const key of state.reactive.identifySelectedFilterKeys()) {
			state = this.excludeInvalidValues(key, state);
			state = this.enforceMultipleSelectionPolicy(key, state);
		}

		return state;
	}

	public normalizeAll(state: FilterSelectionState): FilterSelectionState {
		for (const key of state.reactive.identifySelectedFilterKeys()) {
			state = this.excludeInvalidValues(key, state);
			state = this.enforceMultipleSelectionPolicy(key, state);
		}

		return state;
	}

	public processSelection(payload: IFilterPayload[]): void {
		let state: FilterSelectionState = this.state;
		let stateChanged = false;

		payload.forEach(({ key, value }) => {
			const newState = this.processSelectionOrNull(key, value, state);
			if (newState !== null) {
				state = newState;
				stateChanged = true;
			}
		});

		if (stateChanged) {
			this.publishChangeRequested(state);
		}
	}

	private processSelectionOrNull(
		key: FilterKey,
		value: string,
		state: FilterSelectionState
	): FilterSelectionState | null {
		if (this.filterOptions.isInvalidFor(key, value)) {
			return null;
		}

		if (state.reactive.isSelected(key, value)) {
			state = this.removeFor(key, value, state);
			return state;
		}

		if (this.allowsMultipleValues(key)) {
			state = state.addValueFor(key, value);
		} else {
			state = state.replace(key, [value]);
		}

		return state;
	}

	private excludeInvalidValues(key: FilterKey, state: FilterSelectionState): FilterSelectionState {
		for (const value of state.reactive.getFor(key).filter((v) => this.filterOptions.isInvalidFor(key, v))) {
			state = state.removeFor(key, value);
		}

		return state;
	}

	private enforceMultipleSelectionPolicy(key: FilterKey, state: FilterSelectionState): FilterSelectionState {
		const values = state.reactive.getFor(key);
		if (values.length > 1 && !this.allowsMultipleValues(key)) {
			state = state.replace(key, [values[0]]);
		}

		return state;
	}

	private removeFor(key: FilterKey, value: string, state: FilterSelectionState): FilterSelectionState {
		if (this.cannotBeRemoved(key)) {
			// for required filters on DLP:
			// used replace instead of doing nothing
			// in order to trigger reactivity
			// so that the checkbox is re-checked
			state.reactive.refresh();
			return state;
		}
		state = state.removeFor(key, value);

		return state;
	}

	private cannotBeRemoved(key: FilterKey): boolean {
		return this.filterOptions.isRequiredFor(key) && this.getFor(key).length === 1;
	}

	private allowsMultipleValues(key: FilterKey): boolean {
		return key !== FilterKey.DISCIPLINES && key !== FilterKey.ORGANISATIONS;
	}

	private publishChangeRequested(state: FilterSelectionState, replaced = false): void {
		this.eventAggregationService.publish(new FilterSelectionStateChangedRequestedEvent(state, replaced));
	}

	private commit(other: FilterSelectionState, correlationId: number): void {
		if (this.state.isEqual(other)) {
			this.publishCommitIgnored(correlationId);
			return;
		}

		const normalized = this.normalize(other);

		this.state.reactive.replaceWith(normalized);
		this.publishCommitted(normalized, correlationId);
	}

	private publishCommitIgnored(correlationId: number): void {
		this.eventAggregationService.publish(new FilterSelectionStateCommitIgnoredEvent(correlationId));
	}

	private publishCommitted(state: FilterSelectionState, correlationId: number): void {
		this.eventAggregationService.publish(new FilterSelectionStateCommittedEvent(state, correlationId));
	}
}
